import React from 'react';
import { X, Check } from 'lucide-react';
import { useDesignColors } from '../../../hooks/useDesignColors';
import { MiniAppId, MINI_APP_ICONS } from '../../../utils/miniAppsIcons';

interface MiniAppDetailsProps {
  appId: MiniAppId;
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  isEnabled: boolean;
}

export function MiniAppDetails({ appId, isOpen, onClose, onToggle, isEnabled }: MiniAppDetailsProps) {
  const { colors } = useDesignColors();
  const Icon = MINI_APP_ICONS[appId];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-gray-900/95 rounded-xl border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
              }}
            >
              <Icon className="w-6 h-6" style={{ color: colors.gradientStart }} />
            </div>
            <h3 className="text-xl font-semibold text-white">{getAppTitle(appId)}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {getAppDescription(appId)}
              </p>
              
              <h4 className="text-white font-medium mb-3">Key Features:</h4>
              <ul className="space-y-2 text-gray-300">
                {getAppFeatures(appId).map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-indigo-500/20 flex items-center justify-center mt-0.5 shrink-0">
                      <Check className="w-3 h-3 text-indigo-400" />
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white/5 rounded-xl overflow-hidden">
              <img 
                src={getAppImage(appId)} 
                alt={`${getAppTitle(appId)} preview`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-white/10 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              isEnabled ? 'bg-green-500' : 'bg-gray-600'
            }`}>
              {isEnabled && <Check className="w-4 h-4 text-white" />}
            </div>
            <span className="text-gray-300">
              {isEnabled ? 'This mini app is enabled' : 'This mini app is disabled'}
            </span>
          </div>
          
          <button
            onClick={onToggle}
            className="px-6 py-2 rounded-lg text-white transition-all"
            style={{
              background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
            }}
          >
            {isEnabled ? 'Disable' : 'Enable'} Mini App
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper functions to get app-specific content
function getAppTitle(appId: MiniAppId): string {
  switch (appId) {
    case 'products':
      return 'Product Catalog';
    case 'calendar':
      return 'Appointment Scheduling';
    case 'services':
      return 'Services Showcase';
    case 'videos':
      return 'Video Gallery';
    case 'gallery':
      return 'Photo Gallery';
    case 'realestate':
      return 'Real Estate Properties';
    case 'social':
      return 'Social Media Links';
    case 'order-tracking':
      return 'Order Tracking';
    default:
      return 'Mini App';
  }
}

function getAppDescription(appId: MiniAppId): string {
  switch (appId) {
    case 'products':
      return 'The Product Catalog mini app allows you to showcase your products directly in the chat interface. Users can browse through your catalog, view product details, and get information about pricing and availability without leaving the conversation.';
    case 'calendar':
      return 'The Appointment Scheduling mini app integrates with Calendly to allow users to book appointments directly from the chat. This seamless integration helps convert conversations into scheduled meetings with minimal friction.';
    case 'services':
      return 'The Services Showcase mini app presents your services in an interactive carousel format. Each service can include a title, description, image, and call-to-action button to help users understand your offerings.';
    case 'videos':
      return 'The Video Gallery mini app allows you to share videos from YouTube, Vimeo, or your own sources directly in the chat interface. This creates an engaging multimedia experience for your users.';
    case 'gallery':
      return 'The Photo Gallery mini app displays your images in a beautiful, responsive grid layout. It\'s perfect for showcasing portfolios, products, events, or any visual content that enhances your conversation.';
    case 'realestate':
      return 'The Real Estate Properties mini app is designed specifically for real estate businesses. It displays property listings with details like price, location, size, and features in an interactive interface.';
    case 'social':
      return 'The Social Media Links mini app displays your social media profiles as interactive buttons. Users can easily connect with you on various platforms without leaving the chat.';
    case 'order-tracking':
      return 'The Order Tracking feature allows customers to check the status of their orders by simply entering their order number. This integration works with your e-commerce platform to provide real-time order status updates.';
    default:
      return 'This mini app enhances your chatbot with additional functionality.';
  }
}

function getAppFeatures(appId: MiniAppId): string[] {
  switch (appId) {
    case 'products':
      return [
        'Interactive product catalog with images and descriptions',
        'Product categorization and filtering',
        'Price and availability information',
        'Integration with e-commerce platforms like Shopify and PrestaShop',
        'Customizable display options'
      ];
    case 'calendar':
      return [
        'Seamless Calendly integration',
        'Real-time availability display',
        'Automatic confirmation emails',
        'Calendar synchronization',
        'Customizable appointment types'
      ];
    case 'services':
      return [
        'Interactive service carousel',
        'Rich media presentation with images',
        'Customizable call-to-action buttons',
        'Detailed service descriptions',
        'Organized service categories'
      ];
    case 'videos':
      return [
        'Support for YouTube, Vimeo, and custom videos',
        'Responsive video player',
        'Video thumbnails and previews',
        'Customizable display layouts',
        'Video categorization'
      ];
    case 'gallery':
      return [
        'Responsive image grid or carousel',
        'Lightbox image viewing',
        'Image categorization',
        'High-resolution image support',
        'Customizable display options'
      ];
    case 'realestate':
      return [
        'Detailed property listings',
        'Property search and filtering',
        'Interactive image galleries',
        'Property specifications display',
        'Location information and maps'
      ];
    case 'social':
      return [
        'Customizable social media buttons',
        'Support for all major social platforms',
        'Compact or expanded display options',
        'Direct links to your profiles',
        'Visual branding with platform colors and icons'
      ];
    case 'order-tracking':
      return [
        'Real-time order status updates',
        'Integration with e-commerce platforms',
        'Customizable response templates',
        'Order history access',
        'Shipping and delivery information'
      ];
    default:
      return [
        'Seamless integration with your chatbot',
        'Customizable appearance and behavior',
        'Enhanced user experience',
        'Increased engagement',
        'Improved conversion rates'
      ];
  }
}

function getAppImage(appId: MiniAppId): string {
  // Return placeholder images for each app type
  // In a real implementation, these would be actual screenshots or illustrations
  switch (appId) {
    case 'products':
      return 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=800&h=600&q=80';
    case 'calendar':
      return 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=800&h=600&q=80';
    case 'services':
      return 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&h=600&q=80';
    case 'videos':
      return 'https://images.unsplash.com/photo-1536240478700-b869070f9279?auto=format&fit=crop&w=800&h=600&q=80';
    case 'gallery':
      return 'https://images.unsplash.com/photo-1482245294234-b3f2f8d5f1a4?auto=format&fit=crop&w=800&h=600&q=80';
    case 'realestate':
      return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&h=600&q=80';
    case 'social':
      return 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=800&h=600&q=80';
    case 'order-tracking':
      return 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?auto=format&fit=crop&w=800&h=600&q=80';
    default:
      return 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&h=600&q=80';
  }
}